interface ServiceLineRow {
  itemNo: number;
  name: string;
  qty: string;
  unitPrice: string;
}

interface ServiceLinesTableProps {
  lines: ServiceLineRow[];
  onChange: (lines: ServiceLineRow[]) => void;
  disabled?: boolean;
}

const cellStyle: React.CSSProperties = { padding: "6px 8px", border: "1px solid #dde", textAlign: "left" };
const numCell: React.CSSProperties = { ...cellStyle, textAlign: "right", width: 110 };

function toNum(s: string): number {
  const n = parseFloat(s);
  return isNaN(n) || n < 0 ? 0 : n;
}

export function ServiceLinesTable({ lines, onChange, disabled }: ServiceLinesTableProps): JSX.Element {
  function update(idx: number, field: keyof ServiceLineRow, value: string): void {
    const next = lines.map((l, i) =>
      i === idx ? { ...l, [field]: value } : l
    );
    onChange(next);
  }

  function addLine(): void {
    onChange([...lines, { itemNo: lines.length + 1, name: "", qty: "1", unitPrice: "0" }]);
  }

  function removeLine(idx: number): void {
    const next = lines
      .filter((_, i) => i !== idx)
      .map((l, i) => ({ ...l, itemNo: i + 1 }));
    onChange(next);
  }

  const total = lines.reduce((sum, l) => sum + toNum(l.qty) * toNum(l.unitPrice), 0);

  const inputStyle = (val: string): React.CSSProperties => ({
    width: "100%", border: val === "" ? "1px solid #f99" : "1px solid #ccc",
    borderRadius: 3, padding: "4px 6px", boxSizing: "border-box" as const,
  });

  return (
    <div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ background: "#f5f6fa" }}>
            <th style={{ ...cellStyle, width: 40 }}>#</th>
            <th style={cellStyle}>Description</th>
            <th style={{ ...numCell, width: 80 }}>Qty</th>
            <th style={numCell}>Unit Price (€)</th>
            <th style={numCell}>Net Total (€)</th>
            {!disabled && <th style={{ ...cellStyle, width: 40 }}></th>}
          </tr>
        </thead>
        <tbody>
          {lines.map((l, idx) => (
            <tr key={idx}>
              <td style={cellStyle}>{l.itemNo}</td>
              <td style={cellStyle}>
                <input
                  value={l.name}
                  onChange={(e) => update(idx, "name", e.target.value)}
                  disabled={disabled}
                  placeholder="Service description"
                  style={inputStyle(l.name)}
                />
              </td>
              <td style={numCell}>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={l.qty}
                  onChange={(e) => update(idx, "qty", e.target.value)}
                  disabled={disabled}
                  style={inputStyle(l.qty)}
                />
              </td>
              <td style={numCell}>
                <input
                  type="number"
                  min="0"
                  step="any"
                  value={l.unitPrice}
                  onChange={(e) => update(idx, "unitPrice", e.target.value)}
                  disabled={disabled}
                  style={inputStyle(l.unitPrice)}
                />
              </td>
              <td style={{ ...numCell, fontWeight: 500 }}>
                {(toNum(l.qty) * toNum(l.unitPrice)).toFixed(2)}
              </td>
              {!disabled && (
                <td style={cellStyle}>
                  <button
                    type="button"
                    onClick={() => removeLine(idx)}
                    style={{ background: "none", border: "none", color: "#c00", cursor: "pointer", fontSize: 16 }}
                    aria-label="Remove line"
                  >
                    ×
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={disabled ? 4 : 4} style={{ ...cellStyle, textAlign: "right", fontWeight: 600 }}>
              Total Value (€)
            </td>
            <td style={{ ...numCell, fontWeight: 700 }}>{total.toFixed(2)}</td>
            {!disabled && <td style={cellStyle}></td>}
          </tr>
        </tfoot>
      </table>
      {!disabled && (
        <button
          type="button"
          onClick={addLine}
          style={{ marginTop: 8, padding: "6px 14px", background: "#f5f6fa", border: "1px solid #ccc", borderRadius: 4, cursor: "pointer", fontSize: 13 }}
        >
          + Add service line
        </button>
      )}
    </div>
  );
}

export type { ServiceLineRow };
